export interface ValidationOptions {
  /**
   * For text validation: overrides the default regex.
   */
  regex?: RegExp;
  /**
   * Max string length allowed.
   */
  maxLength?: number;
  /**
   * For number validation: whether to allow negative numbers.
   */
  allowNegative?: boolean;
}

export interface ValidationResult {
  isValid: boolean;
  errorMessage?: string;
}

/**
 * Validates text input to only allow upper/lowercase alphabets, numbers, space, and hyphens.
 * Additional check for maxLength and custom regex if provided.
 */
export const validateText = (
  value: string,
  options: ValidationOptions = {},
): ValidationResult => {
  const { regex, maxLength } = options;

  if (maxLength && value.length > maxLength) {
    return {
      isValid: false,
      errorMessage: `Maximum length of ${maxLength} characters exceeded.`,
    };
  }

  // If a custom regex is provided, use it instead of the default.
  if (regex) {
    if (!regex.test(value)) {
      return { isValid: false, errorMessage: 'Invalid format.' };
    }
  } else {
    // Default regex: Upper and lower case alphabets, numbers, space, and - sign.
    // Allows empty string, so we use * instead of + if we want to allow empty.
    // Actually, usually users type character by character, so we check if all characters match this.
    // ^[a-zA-Z0-9\s-]*$
    const defaultRegex = /^[a-zA-Z0-9\s-]*$/;
    if (!defaultRegex.test(value)) {
      return {
        isValid: false,
        errorMessage: 'Only letters, numbers, spaces, and hyphens are allowed.',
      };
    }
  }

  return { isValid: true };
};

/**
 * Validates number input. The input field should ideally be type="text" to prevent browser native validation issues.
 * Checks for a valid number, negative allowance, and max length.
 */
export const validateNumber = (
  value: string,
  options: ValidationOptions = {},
): ValidationResult => {
  const { maxLength, allowNegative = false } = options;

  if (maxLength && value.length > maxLength) {
    return {
      isValid: false,
      errorMessage: `Maximum length of ${maxLength} characters exceeded.`,
    };
  }

  // Allow empty string to pass format validation (often handled as required elsewhere or empty is okay)
  if (value.trim() === '') {
    return { isValid: true };
  }

  // Regex for a valid number. Allows decimals.
  const numberRegex = allowNegative ? /^-?\d*(\.\d+)?$/ : /^\d*(\.\d+)?$/;

  if (!numberRegex.test(value)) {
    return {
      isValid: false,
      errorMessage: allowNegative
        ? 'Must be a valid number.'
        : 'Must be a positive number.',
    };
  }

  // Check if it resolves to NaN when parsed
  const num = Number(value);
  if (isNaN(num)) {
    return { isValid: false, errorMessage: 'Must be a valid number.' };
  }

  if (!allowNegative && num < 0) {
    return {
      isValid: false,
      errorMessage: 'Negative numbers are not allowed.',
    };
  }

  return { isValid: true };
};
